import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, Navigate, type Path, useLocation, useNavigate } from 'react-router';
import { AuthError, useAuthServices } from '../authServices';
import { selectAuthStatus } from '../AuthStore';
import { AuthLayout } from '../AuthLayout';
import { Icon } from '../../common/Icon';

type SignInState = { from?: Partial<Path>; confirmed?: boolean; passwordReset?: boolean } | null;

/** QZ-D28: quizdb's own sign-in form, talking to Cognito through `useAuthServices()`. */
export const SignInPage = () => {
  const { t } = useTranslation();
  const { cognito, session } = useAuthServices();
  const navigate = useNavigate();
  const location = useLocation();
  const status = useSelector(selectAuthStatus);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const state = location.state as SignInState;
  const from = state?.from;
  const target = from ?? '/';

  if (status === 'signedIn') {
    return <Navigate to={target} replace />;
  }

  const errorMessage = (thrown: unknown) =>
    thrown instanceof AuthError ? t(`auth.error.${thrown.code}`) : t('auth.error.Unknown');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      const tokens = await cognito.signIn(email, password);
      await session.start(tokens);
      navigate(target, { replace: true });
    } catch (thrown) {
      if (thrown instanceof AuthError && thrown.code === 'UserNotConfirmed') {
        navigate(`/signup/confirm?email=${encodeURIComponent(email)}`);
        return;
      }
      setError(errorMessage(thrown));
      setBusy(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(undefined);
    setBusy(true);
    try {
      await cognito.startGoogleSignIn();
    } catch (thrown) {
      setError(errorMessage(thrown));
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      footer={
        <>
          {t('auth.signIn.noAccount')} <Link to="/signup">{t('auth.signIn.toSignUp')}</Link>
        </>
      }
    >
      <h1>{t('auth.signIn.heading')}</h1>
      <p className="qz-sub">{t('auth.signIn.tagline')}</p>
      {state?.confirmed && (
        <div className="alert alert-success">
          <Icon name="circle-check" />
          <div>{t('auth.signIn.confirmed')}</div>
        </div>
      )}
      {state?.passwordReset && (
        <div className="alert alert-success">
          <Icon name="circle-check" />
          <div>{t('auth.forgot.done')}</div>
        </div>
      )}
      {error && (
        <div className="alert alert-danger">
          <Icon name="circle-exclamation" />
          <div>{error}</div>
        </div>
      )}
      <button
        type="button"
        className="btn btn-outline-secondary w-100"
        disabled={busy}
        onClick={handleGoogleSignIn}
      >
        <Icon name="google" brand /> {t('auth.signIn.google')}
      </button>
      <div className="qz-divider">{t('auth.signIn.orEmail')}</div>
      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label htmlFor="signin-email" className="form-label">
            {t('auth.email')}
          </label>
          <input
            id="signin-email"
            type="email"
            autoComplete="username"
            className="form-control"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="mb-3">
          <div className="d-flex justify-content-between">
            <label htmlFor="signin-password" className="form-label">
              {t('auth.password')}
            </label>
            <Link to="/forgot-password" className="qz-small">
              {t('auth.signIn.toForgot')}
            </Link>
          </div>
          <input
            id="signin-password"
            type="password"
            autoComplete="current-password"
            className="form-control"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <button type="submit" className="btn btn-primary w-100" disabled={busy}>
          {t('auth.signIn.submit')}
        </button>
      </form>
    </AuthLayout>
  );
};

export default SignInPage;
